<template>
  <div>
    <b-row align-v="center" class="d-flex align-items-center">
      <b-col cols="6" class="mb-md-0 mb-2">
        <label style="text-transform: capitalize">{{
          $t("maintenance.maintenance_companies")
        }}</label>
        <v-select
          :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
          :value="maintenanceFilter"
          :options="maintenanceOptions"
          class="w-100"
          :reduce="(val) => val.value"
          @input="(val) => $emit('update:maintenanceFilter', val)"
        />
      </b-col>

      <!-- Units -->
      <b-col cols="3" md="3" class="mb-md-0 mb-2">
        <label style="text-transform: capitalize">
          {{ $tc("unit.title", 2) }}
        </label>
        <base-select-pagination
          url="/v1/unit"
          input-id="id_unit"
          :option-mapper="unitMapperOption"
          :value="unit"
          @input="onChangeUnit"
        />
      </b-col>

      <!-- Status -->
      <b-col cols="3" md="3" class="mb-md-0 mb-2">
        <label style="text-transform: capitalize">
          {{ $t("service_orders.status") }}
        </label>
        <v-select
          :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
          :value="statusFilter"
          :options="translatedStatusOptions"
          class="w-100"
          :reduce="(val) => val.value"
          @input="(val) => $emit('update:statusFilter', val)"
        />
      </b-col>
    </b-row>

      <!-- Date filter radio buttons -->
    <b-row class="align-items-end">
      <b-col cols="6" md="6" class="mb-md-0 mb-2 mt-2">
        <b-form-group :label="$t('service_orders.date_filters')">
          <div style="display: flex; align-items: center">
            <b-form-radio-group
              id="radio-group-1"
              v-model="selectedDateFilter"
              :options="dateOptions"
              name="date-filter"
              @input="
                (val) => {
                  if (val !== '') {
                    test();
                  }
                }
              "
            />
            <b-button
              variant="success"
              @click="clearSelection"
              style="text-transform: capitalize"
            >
              {{ $t("service_orders.delete_selection") }}
            </b-button>
          </div>
        </b-form-group>
        <div
          style="color: red; text-transform: capitalize"
          v-show="valiteCheck"
        >
          {{ $t("service_orders.required_fields_to_filter_by_date") }}
        </div>
      </b-col>

      <!-- Start date -->
      <b-col cols="3" md="3" class="mb-md-0 mb-2">
        <b-form-group
          :label="$t('log_microservice.start_date')"
          label-for="start_date"
        >
          <flat-pickr
            id="start_date"
            class="form-control"
            v-model="start_date"
            @input="
              (val) => {
                if (val !== '') {
                  test();
                }
              }
            "
            :config="{ enableTime: true, dateFormat: 'Y-m-d H:i' }"
          />
        </b-form-group>
        <div
          style="color: red; text-transform: capitalize"
          v-show="valiteCheck"
        >
          {{ $t("service_orders.required_fields_to_filter_by_date") }}
        </div>
      </b-col>

      <!-- End date -->
      <b-col cols="3" md="3" class="mb-md-0 mb-2">
        <b-form-group
          :label="$t('log_microservice.end_date')"
          label-for="end_date"
        >
          <flat-pickr
            id="end_date"
            class="form-control"
            v-model="end_date"
            @input="
              (val) => {
                if (val !== '') {
                  test();
                }
              }
            "
            :config="{ enableTime: true, dateFormat: 'Y-m-d H:i' }"
          />
        </b-form-group>
        <div
          style="color: red; text-transform: capitalize"
          v-show="valiteCheck"
        >
          {{ $t("service_orders.required_fields_to_filter_by_date") }}
        </div>
      </b-col>
    </b-row>

    <b-row class="align-items-end">
      <!-- Status legend -->
      <b-col cols="6" class="mb-2">
        <ul>
          <li
            style="list-style: none"
            v-for="(item, index) in statusItemsOrdered"
            :key="index"
          >
            <div style="width: 100%; display: flex">
              <div>
                <span>{{ index + 1 }}</span>
              </div>
              <div
                :style="{
                  marginLeft: '5px',
                  backgroundColor: item.color,
                  borderRadius: '50px',
                  height: '18px',
                  width: '18px',
                }"
              ></div>
              <div style="margin-left: 5px">
                <span>{{ $t(`service_orders.${item.status}`) }}</span>
              </div>
            </div>
          </li>
        </ul>
      </b-col>

      <b-col cols="3" class="mb-2">
        <label style="text-transform: capitalize">
          {{ $t("maintenance.maintenance_crew") }}
        </label>
        <v-select
          :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
          :value="maintenanceCrewFilter"
          :options="maintenanceCrewOptions"
          class="w-100"
          :reduce="(val) => val.value"
          @input="(val) => $emit('update:maintenanceCrewFilter', val)"
        />
      </b-col>

      <b-col cols="3" class="mb-2">
        <label class="text-capitalize"> {{ $t("maintenance.template_name") }}</label>
        <v-select
          :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
          :value="templateFilter"
          :options="templateOptions"
          class="w-100"
          :reduce="(val) => val.value"
          @input="(val) => $emit('update:templateFilter', val)"
          append-to-body
          :calculate-position="withPopper"
        />
      </b-col>
    </b-row>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BRow,
  BCol,
  BButton,
  BFormGroup,
  BFormInvalidFeedback,
  BFormRadioGroup,
} from "bootstrap-vue";
import flatPickr from "vue-flatpickr-component";
import vSelect from "vue-select";
import { ref, watch, onMounted } from "@vue/composition-api";
import SelectPaginateUnit from "../components/SelectPaginateUnit.vue";
import popperHelper from "@/libs/popper-helper";
import BaseSelectPagination from "@/components/Form/BaseSelectPagination.vue";
import store from '@/store'

export default {
  components: {
    flatPickr,
    BFormInvalidFeedback,
    BFormGroup,
    BRow,
    BCol,
    BCard,
    BCardHeader,
    BCardBody,
    BButton,
    vSelect,
    BFormRadioGroup,
    SelectPaginateUnit,
    BaseSelectPagination,
  },
  props: {
    maintenanceOptions: {
      type: Array,
      default: null,
    },
    maintenanceFilter: {
      type: [Array, Number],
      default: null,
    },
    unitOptions: {
      type: Array,
      default: null,
    },
    unitFilter: {
      type: [Array, Number],
      default: null,
    },
    statusOptions: {
      type: Array,
      default: null,
    },
    statusFilter: {
      type: Number,
      default: null,
    },
    dateFilter: {
      type: String,
      default: null,
    },
    startDate: {
      type: String,
      default: null,
    },
    endDate: {
      type: String,
      default: null,
    },
    statusItems: {
      type: Array,
      default: null,
    },
    maintenanceCrewOptions: {
      type: Array,
      default: null,
    },
    maintenanceCrewFilter: {
      type: [Array, Number],
      default: null,
    },
    templateOptions: {
      type: Array,
      default: null,
    },
    templateFilter: {
      type: [Array, Number],
      default: null,
    },
  },
  data() {
    return {
      id_unit: null,
      translatedStatusOptions: [],
      selectedDateFilter: "",
      start_date: "",
      end_date: "",
      valiteCheck: false,
      valiteStar: false,
      valiteEnd: false,
      dateOptions: [
        {
          text: this.$t("service_orders.attention_date"),
          value: "attention_date",
        },
        { text: this.$t("service_orders.status_change_date"), value: "status" },
        { text: this.$t("date.created_at"), value: "created_at" },
      ],
    };
  },
  methods: {
    clearSelection() {
      this.$emit("update:dateFilter", null);
      this.$emit("update:startDate", null);
      this.$emit("update:endDate", null);
      this.selectedDateFilter = "";
      this.start_date = "";
      this.end_date = "";
    },
    test() {
      this.selectedDateFilter !== ""
        ? (this.valiteCheck = false)
        : (this.valiteCheck = true);
      this.start_date !== ""
        ? (this.valiteStar = false)
        : (this.valiteStar = true);
      this.end_date !== "" ? (this.valiteEnd = false) : (this.valiteEnd = true);
      if (
        this.valiteCheck === false &&
        this.valiteStar === false &&
        this.valiteEnd === false
      ) {
        this.$emit("update:dateFilter", this.selectedDateFilter);
        this.$emit("update:startDate", this.start_date);
        this.$emit("update:endDate", this.end_date);
      }
    },
    translateStatusOptions() {
      if (!this.statusOptions) {
        this.translatedStatusOptions = [];
        return;
      }
      this.translatedStatusOptions = this.statusOptions.map(
        (service_orders_status) => ({
          value: service_orders_status.value,
          label: this.$t(`service_orders.${service_orders_status.label}`),
        })
      );
    },
    changeUnit() {
      this.$emit("update:unitFilter", this.id_unit);
    },
  },
  watch: {
    statusOptions: {
      handler: "translateStatusOptions",
      immediate: true,
    },
    id_unit: "changeUnit",
  },
  mounted() {
    this.translateStatusOptions();
  },
  setup(props, { emit }) {
    const unit = ref(null);
    
    const onChangeUnit = (value) => {
      unit.value = value;

      emit('update:unitFilter', value ? value.id : null);
    };

    const unitMapperOption = (item) => ({
      ...item,
      id: item.id,
      label: item.description,
    });

    watch(
      () => props.unitFilter,
      (newVal) => {
        if (!newVal) {
          unit.value = null
        }
      }
    );

    onMounted(() => {
      if (props.unitFilter) {
        const id = Array.isArray(props.unitFilter) ? props.unitFilter[0] : props.unitFilter

        store.dispatch('unit/get', id).then(response => {
          unit.value = unitMapperOption(response.data)
        })
      }
    });
    
    return {
      unit,
      unitMapperOption,
      onChangeUnit,
      withPopper: popperHelper.withPopper,
    };
  },
  computed: {
    statusItemsOrdered() {
      if(!this.statusItems) return [];

      const statusItemsOrdered = [
        "pending",
        "in_progress",
        "in_review",
        "finished",
        "canceled",
      ].map(status => {
        const statusItem = this.statusItems.find(item => item.status === status);
        return statusItem;
      }).filter(item => item);
      
      return statusItemsOrdered;
    }
  }
};
</script>
